/**
 * Browser compatibility detection and error handling service
 */

export interface CompatibilityResult {
  /** Whether the feature is supported */
  supported: boolean;
  /** Error message if not supported */
  error?: string;
  /** Warning message for partial support */
  warning?: string;
  /** Suggested alternatives or workarounds */
  suggestions?: string[];
}

export interface BrowserInfo {
  /** Browser name */
  name: string;
  /** Browser version */
  version: string;
  /** Operating system */
  os: string;
  /** Whether it's a mobile device */
  isMobile: boolean;
}

/**
 * Service for detecting browser compatibility and handling errors
 */
export class BrowserCompatibility {
  /**
   * Check Web Audio API support
   */
  static checkWebAudioSupport(): CompatibilityResult {
    // Check for AudioContext
    const hasAudioContext = typeof AudioContext !== 'undefined' || typeof (window as any).webkitAudioContext !== 'undefined';
    
    if (!hasAudioContext) {
      return {
        supported: false,
        error: 'Web Audio API is not supported in this browser.',
        suggestions: [
          'Please use a modern browser like Chrome 34+, Firefox 25+, Safari 14.1+, or Edge 79+',
          'Update your browser to the latest version',
        ],
      };
    }

    // Check for ScriptProcessorNode (deprecated but still needed for compatibility)
    const hasScriptProcessor = typeof AudioContext !== 'undefined' && 
                               AudioContext.prototype.createScriptProcessor !== undefined;

    if (!hasScriptProcessor) {
      return {
        supported: false,
        error: 'Audio processing capabilities are not available in this browser.',
        suggestions: [
          'This browser version may be too old',
          'Please update to a newer version or use a different browser',
        ],
      };
    }

    return { supported: true };
  }

  /**
   * Check MediaDevices API support
   */
  static checkMediaDevicesSupport(): CompatibilityResult {
    if (typeof navigator === 'undefined') {
      return {
        supported: false,
        error: 'Navigator API is not available.',
      };
    }

    if (!navigator.mediaDevices) {
      return {
        supported: false,
        error: 'MediaDevices API is not supported in this browser.',
        suggestions: [
          'Please use a modern browser with HTTPS',
          'MediaDevices API requires a secure context (HTTPS)',
        ],
      };
    }

    if (!navigator.mediaDevices.getUserMedia) {
      return {
        supported: false,
        error: 'getUserMedia is not supported in this browser.',
        suggestions: [
          'Please use Chrome 53+, Firefox 36+, Safari 11+, or Edge 12+',
          'Update your browser to the latest version',
        ],
      };
    }

    return { supported: true };
  }

  /**
   * Check WebSocket support
   */
  static checkWebSocketSupport(): CompatibilityResult {
    if (typeof WebSocket === 'undefined') {
      return {
        supported: false,
        error: 'WebSocket is not supported in this browser.',
        suggestions: [
          'Please use a modern browser',
          'WebSocket is supported in all modern browsers',
        ],
      };
    }

    return { supported: true };
  }

  /**
   * Check if the page is served over HTTPS
   */
  static checkSecureContext(): CompatibilityResult {
    const isSecure = typeof window !== 'undefined' && 
                     (window.location.protocol === 'https:' || 
                      window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1');

    if (!isSecure) {
      return {
        supported: false,
        error: 'This application requires a secure context (HTTPS).',
        suggestions: [
          'Please access this application over HTTPS',
          'MediaDevices API requires HTTPS for security reasons',
        ],
      };
    }

    return { supported: true };
  }

  /**
   * Comprehensive compatibility check
   */
  static checkAllFeatures(): { [key: string]: CompatibilityResult } {
    return {
      webAudio: this.checkWebAudioSupport(),
      mediaDevices: this.checkMediaDevicesSupport(),
      webSocket: this.checkWebSocketSupport(),
      secureContext: this.checkSecureContext(),
    };
  }

  /**
   * Get browser information
   */
  static getBrowserInfo(): BrowserInfo {
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    
    // Detect browser
    let name = 'Unknown';
    let version = 'Unknown';
    
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      name = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Firefox')) {
      name = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      name = 'Safari';
      const match = userAgent.match(/Version\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Edg')) {
      name = 'Edge';
      const match = userAgent.match(/Edg\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    }

    // Detect OS
    let os = 'Unknown';
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iOS')) os = 'iOS';

    // Detect mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

    return { name, version, os, isMobile };
  }

  /**
   * Generate compatibility report
   */
  static generateCompatibilityReport(): {
    browserInfo: BrowserInfo;
    features: { [key: string]: CompatibilityResult };
    overallSupported: boolean;
    criticalIssues: string[];
    warnings: string[];
  } {
    const browserInfo = this.getBrowserInfo();
    const features = this.checkAllFeatures();
    
    const criticalIssues: string[] = [];
    const warnings: string[] = [];
    
    let overallSupported = true;
    
    for (const [featureName, result] of Object.entries(features)) {
      if (!result.supported) {
        overallSupported = false;
        if (result.error) {
          criticalIssues.push(`${featureName}: ${result.error}`);
        }
      } else if (result.warning) {
        warnings.push(`${featureName}: ${result.warning}`);
      }
    }

    return {
      browserInfo,
      features,
      overallSupported,
      criticalIssues,
      warnings,
    };
  }
}

/**
 * Permission error handling utilities
 */
export class PermissionErrorHandler {
  /**
   * Get user-friendly error message for permission errors
   */
  static getPermissionErrorMessage(error: Error): {
    title: string;
    message: string;
    instructions: string[];
    recoverable: boolean;
  } {
    switch (error.name) {
      case 'NotAllowedError':
        return {
          title: 'Microphone Access Denied',
          message: 'You have denied access to your microphone. This application needs microphone access to provide transcription services.',
          instructions: [
            'Click the microphone icon in your browser\'s address bar',
            'Select "Allow" for microphone access',
            'Refresh the page and try again',
            'If you don\'t see the icon, check your browser settings for this site',
          ],
          recoverable: true,
        };

      case 'NotFoundError':
        return {
          title: 'No Microphone Found',
          message: 'No microphone device was found on your system.',
          instructions: [
            'Connect a microphone to your device',
            'Check that your microphone is properly connected',
            'Ensure your microphone drivers are installed',
            'Try refreshing the page after connecting a microphone',
          ],
          recoverable: true,
        };

      case 'NotReadableError':
        return {
          title: 'Microphone In Use',
          message: 'Your microphone is currently being used by another application.',
          instructions: [
            'Close other applications that might be using your microphone',
            'Check for video conferencing apps, recording software, or other browser tabs',
            'Restart your browser if the issue persists',
            'Try again after closing conflicting applications',
          ],
          recoverable: true,
        };

      case 'OverconstrainedError':
        return {
          title: 'Microphone Constraints Not Met',
          message: 'Your microphone does not support the required audio settings.',
          instructions: [
            'Try using a different microphone if available',
            'Check your microphone specifications',
            'Update your audio drivers',
            'Contact support if you continue to experience issues',
          ],
          recoverable: false,
        };

      case 'SecurityError':
        return {
          title: 'Security Error',
          message: 'Access to microphone was blocked due to security restrictions.',
          instructions: [
            'Ensure you are accessing this site over HTTPS',
            'Check your browser security settings',
            'Try using a different browser',
            'Contact your system administrator if on a managed device',
          ],
          recoverable: false,
        };

      case 'AbortError':
        return {
          title: 'Request Aborted',
          message: 'The microphone access request was aborted.',
          instructions: [
            'Try requesting microphone access again',
            'Ensure you respond to the permission prompt',
            'Refresh the page if the issue persists',
          ],
          recoverable: true,
        };

      default:
        return {
          title: 'Microphone Access Error',
          message: `An unexpected error occurred: ${error.message || 'Unknown error'}`,
          instructions: [
            'Try refreshing the page',
            'Check your browser console for more details',
            'Try using a different browser',
            'Contact support if the issue persists',
          ],
          recoverable: true,
        };
    }
  }

  /**
   * Check current permission status
   */
  static async checkPermissionStatus(): Promise<{
    state: 'granted' | 'denied' | 'prompt' | 'unknown';
    canRequest: boolean;
  }> {
    try {
      if (typeof navigator === 'undefined' || !navigator.permissions) {
        return { state: 'unknown', canRequest: true };
      }

      const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      
      return {
        state: permission.state as 'granted' | 'denied' | 'prompt',
        canRequest: permission.state !== 'denied',
      };
    } catch (error) {
      // Permissions API not supported or query failed
      return { state: 'unknown', canRequest: true };
    }
  }

  /**
   * Monitor permission changes
   */
  static monitorPermissionChanges(
    onPermissionChange: (state: 'granted' | 'denied' | 'prompt') => void
  ): () => void {
    let cleanup: (() => void) | null = null;

    const setupMonitoring = async () => {
      try {
        if (typeof navigator === 'undefined' || !navigator.permissions) {
          return;
        }

        const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        
        const handleChange = () => {
          onPermissionChange(permission.state as 'granted' | 'denied' | 'prompt');
        };

        permission.addEventListener('change', handleChange);
        
        cleanup = () => {
          permission.removeEventListener('change', handleChange);
        };
      } catch (error) {
        // Permissions API not supported, ignore
      }
    };

    setupMonitoring();

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }
}